import {
  Bot,
  CircleHelp,
  DoorOpen,
  KeyRound,
  LockKeyhole,
  Shield,
  UserPlus,
  UserRound,
  Volume2,
  VolumeX,
  WandSparkles,
  X,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { nextAutomaticSlideDeadline } from "../automatic";
import { type BoardCanvasHandle, BoardCanvas } from "../BoardCanvas";
import { type EffectSettings, vibrateForEffect } from "../effects";
import { type GrowthProfile, type GrowthSkill, type RunView } from "../types";
import { readAutoModePreference, writeAutoModePreference } from "../ui/autoPreference";
import { EquipmentDropNotifier, IncomingHitFeedback } from "../ui/CombatFeedback";
import { AttackElementMark, TargetElementMark } from "../ui/Elements";
import { EnemyDetail, monsterTraitSummary } from "../ui/EnemyDetails";
import { formatDisplayNumber } from "../ui/format";
import { battleSceneForZone, spriteForMonster } from "../ui/gameArt";
import { Meter, ResourceIcon, Status } from "../ui/Resources";
import {
  SkillButton,
  skillCooldownState,
  SkillIcon,
  skillResourceLimitText,
} from "../ui/SkillControls";
import { findAutomaticSlide } from "../wasm";
import { ChestEncounter, ObstacleEncounter } from "./Encounters";

export function GameScreen({
  view,
  clockNow,
  growth,
  busy,
  requestQueued,
  simReady,
  colorBlind,
  effectSettings,
  muted,
  onToggleMuted,
  onSlide,
  onLockRow,
  onSkill,
  onChest,
  onRetreat,
  guest,
  playerName,
  onBind,
}: {
  view: RunView;
  clockNow: () => number;
  growth: GrowthProfile;
  busy: boolean;
  requestQueued: boolean;
  simReady: boolean;
  colorBlind: boolean;
  effectSettings: EffectSettings;
  muted: boolean;
  onToggleMuted: () => void;
  onSlide: (
    axis: "row" | "col",
    index: number,
    distance: number,
    automated: boolean,
  ) => Promise<void>;
  onLockRow: (row: number) => void;
  onSkill: (skill: string) => void;
  onChest: (open: boolean) => Promise<void>;
  onRetreat: () => void;
  guest: boolean;
  playerName: string;
  onBind: () => void;
}) {
  const run = view.run;
  const equippedSkills = (run.skillLoadout ?? growth.skillSlots)
    .map((skillId) => growth.skills.find((skill) => skill.id === skillId))
    .filter((skill): skill is GrowthSkill => Boolean(skill));
  const [clock, setClock] = useState(clockNow);
  const [inputQueued, setInputQueued] = useState(false);
  const [autoPreference, setAutoPreference] = useState(() => ({
    runId: run.id,
    enabled: readAutoModePreference(),
  }));
  const autoRequested = autoPreference.runId === run.id && autoPreference.enabled;
  const autoMode = autoRequested && simReady;
  const setAutoMode = (enabled: boolean) => setAutoPreference({ runId: run.id, enabled });
  const [dismissedGuestPrompt, setDismissedGuestPrompt] = useState<string | null>(null);
  const [inspectedEnemy, setInspectedEnemy] = useState<{
    id: string;
    index: number;
    scope: string;
  } | null>(null);
  const inputEpoch = useRef(0);
  const autoCursor = useRef(0);
  const autoSubmitting = useRef(false);
  const autoNextSlideAt = useRef(0);
  const autoChestRevision = useRef(-1);
  const lastVibrationKey = useRef("");
  const boardCanvasRef = useRef<BoardCanvasHandle>(null);
  useEffect(() => {
    const timer = window.setInterval(() => setClock(clockNow()), 100);
    return () => window.clearInterval(timer);
  }, [clockNow]);
  useEffect(() => {
    setAutoPreference({
      runId: run.id,
      enabled: readAutoModePreference(),
    });
    autoCursor.current = 0;
    autoNextSlideAt.current = 0;
    autoChestRevision.current = -1;
    return () => {
      inputEpoch.current++;
    };
  }, [run.id]);
  const freeSlideReady =
    run.mechanics.freeSlideCooldownMs > 0 && clock >= (run.freeSlideReadyAt ?? 0);
  const authorityLocked = clock < run.lockedUntil && !freeSlideReady;
  const locked =
    inputQueued ||
    requestQueued ||
    !["combat", "swarm", "elite", "obstacle"].includes(run.encounter);
  const prepLeft = Math.max(0, run.prepUntil - clock);
  const visibleMonsters = (run.monsters ?? [run.monster]).filter(Boolean).slice(0, 4);
  const enemyScope = `${run.id}:${run.depth}:${run.encounter}`;
  const inspectedMonster =
    inspectedEnemy?.scope === enemyScope ? visibleMonsters[inspectedEnemy.index] : undefined;
  const detailMonster =
    inspectedMonster?.id === inspectedEnemy?.id && inspectedMonster && inspectedMonster.hp > 0
      ? inspectedMonster
      : undefined;
  useEffect(() => {
    if (!detailMonster) setInspectedEnemy(null);
  }, [detailMonster]);
  const depthProgress = Math.max(8, (((run.depth - 1) % 10) + 1) * 10);
  const showGuestRegistrationPrompt = guest && run.depth >= 10 && dismissedGuestPrompt !== run.id;
  const comboStacks = run.combo ?? 0;
  const comboWindowMs = Math.max(1, run.comboWindowMs ?? 4000);
  const comboExpiresAt = run.comboExpiresAt ?? 0;
  const comboClock =
    comboExpiresAt > 0 && clock < run.lockedUntil
      ? Math.min(run.lockedUntil, comboExpiresAt)
      : clock;
  const comboRemainingMs =
    comboStacks > 0
      ? comboExpiresAt > 0
        ? Math.max(0, comboExpiresAt - comboClock)
        : comboWindowMs
      : 0;
  const combo = comboRemainingMs > 0 ? comboStacks : 0;
  const comboProgress = Math.min(100, (comboRemainingMs / comboWindowMs) * 100);
  const comboRemainingSeconds = formatDisplayNumber(comboRemainingMs / 1000);
  const automaticSlideIntervalMs = Math.max(1, run.automaticSlideIntervalMs || 1000);
  const retreatRemainingSec = Math.max(0, Math.ceil((run.retreatAvailableAt - clock) / 1000));
  const retreatLocked = retreatRemainingSec > 0;
  const recentIncomingHits = (run.lastIncomingHits ?? []).filter(
    (hit) => hit.at > 0 && Math.abs(clock - hit.at) < 900,
  );

  useEffect(() => {
    const incoming = recentIncomingHits[recentIncomingHits.length - 1];
    const incomingAt = incoming?.at ?? 0;
    const key = String(incomingAt);
    if (key === lastVibrationKey.current || incomingAt <= 0) return;
    lastVibrationKey.current = key;
    const strength = Math.min(1.4, 0.65 + ((incoming?.damage ?? 0) / Math.max(1, run.maxHp)) * 2);
    vibrateForEffect(effectSettings, strength);
  }, [effectSettings, recentIncomingHits, run.maxHp]);

  const incomingMonsterIDs = new Set(recentIncomingHits.map((hit) => hit.sourceId));
  const recentOutgoingDamage = Boolean(
    run.lastDamage && run.lastDamageAt && Math.abs(clock - run.lastDamageAt) < 900,
  );
  const outgoingTargetVisible = visibleMonsters.some(
    (monster) => monster?.id === run.lastDamageTarget,
  );
  const submitOrQueueSlide = useCallback(
    async (axis: "row" | "col", index: number, distance: number, automated = false) => {
      const epoch = inputEpoch.current;
      const waitMs = authorityLocked ? Math.max(0, run.lockedUntil - clockNow() + 24) : 0;
      if (waitMs > 0) {
        setInputQueued(true);
        await new Promise<void>((resolve) => window.setTimeout(resolve, waitMs));
      }
      if (epoch !== inputEpoch.current) return;
      try {
        await onSlide(axis, index, distance, automated);
      } finally {
        if (epoch === inputEpoch.current) setInputQueued(false);
      }
    },
    [authorityLocked, clockNow, onSlide, run.lockedUntil],
  );
  const automaticRuntime = useRef({
    autoMode,
    authorityLocked,
    busy,
    inputQueued,
    requestQueued,
    run,
    simReady,
    submitOrQueueSlide,
  });
  automaticRuntime.current = {
    autoMode,
    authorityLocked,
    busy,
    inputQueued,
    requestQueued,
    run,
    simReady,
    submitOrQueueSlide,
  };
  const retreat = () => {
    if (retreatLocked) return;
    setAutoMode(false);
    inputEpoch.current++;
    setInputQueued(false);
    onRetreat();
  };
  useEffect(() => {
    if (!autoMode) {
      autoNextSlideAt.current = 0;
      return;
    }
    autoNextSlideAt.current = nextAutomaticSlideDeadline(
      autoNextSlideAt.current,
      clockNow(),
      automaticSlideIntervalMs,
      automaticRuntime.current.run.lastAutomaticSlideAt,
    );
    const timer = window.setInterval(
      () => {
        const current = automaticRuntime.current;
        const now = clockNow();
        autoNextSlideAt.current = nextAutomaticSlideDeadline(
          autoNextSlideAt.current,
          now,
          automaticSlideIntervalMs,
          current.run.lastAutomaticSlideAt,
        );
        if (
          !current.autoMode ||
          now < autoNextSlideAt.current ||
          autoSubmitting.current ||
          current.busy ||
          current.inputQueued ||
          current.requestQueued ||
          current.authorityLocked ||
          !current.simReady ||
          !["combat", "swarm", "elite", "obstacle"].includes(current.run.encounter)
        )
          return;
        const slide = findAutomaticSlide(
          current.run.board,
          {
            wrapMatches: current.run.mechanics.wrapMatches,
            woodOreUnion: current.run.mechanics.woodOreUnion,
          },
          autoCursor.current,
        );
        autoNextSlideAt.current = now + automaticSlideIntervalMs;
        if (!slide) return;
        autoCursor.current = slide.nextCursor;
        autoSubmitting.current = true;
        const submission = boardCanvasRef.current
          ? boardCanvasRef.current.playAutomatedSlide(
              slide.axis,
              slide.index,
              slide.distance,
              current.run.boardHash,
            )
          : current
              .submitOrQueueSlide(slide.axis, slide.index, slide.distance, true)
              .then(() => true);
        void submission.finally(() => {
          autoSubmitting.current = false;
        });
      },
      Math.min(100, automaticSlideIntervalMs),
    );
    return () => window.clearInterval(timer);
  }, [autoMode, automaticSlideIntervalMs, clockNow]);
  useEffect(() => {
    if (!autoMode) {
      autoChestRevision.current = -1;
      return;
    }
    if (
      busy ||
      requestQueued ||
      run.encounter !== "chest" ||
      autoChestRevision.current === run.revision
    )
      return;
    autoChestRevision.current = run.revision;
    void onChest(run.keys >= (run.chestCost ?? 3));
  }, [
    autoMode,
    busy,
    onChest,
    requestQueued,
    run.chestCost,
    run.encounter,
    run.keys,
    run.revision,
  ]);
  return (
    <main className="game-layout">
      {detailMonster && (
        <EnemyDetail
          name={detailMonster.name}
          image={spriteForMonster(detailMonster)}
          resistance={detailMonster.resistanceElement}
          weakness={detailMonster.weaknessElement}
          onClose={() => setInspectedEnemy(null)}
          stats={[
            { label: "生命", value: `${Math.max(0, detailMonster.hp)} / ${detailMonster.maxHp}` },
            { label: "攻击", value: String(detailMonster.attack) },
            { label: "护甲", value: String(detailMonster.armor) },
            {
              label: "攻击间隔",
              value: `${formatDisplayNumber(detailMonster.attackPeriodMs / 1000)}s`,
            },
            ...(detailMonster.boss
              ? [{ label: "层主阶段", value: `${detailMonster.phase} / 3` }]
              : []),
          ]}
          traits={monsterTraitSummary(
            detailMonster,
            view.machineConfig.reflectDamageAttackMultiplierCap,
          )
            .split("；")
            .filter(Boolean)}
        />
      )}
      <EquipmentDropNotifier drops={run.equipmentDrops ?? []} scopeKey={run.id} />
      <section className="game-column">
        <section
          className={`battlefield palette-${run.zonePalette}${run.monster?.boss ? " is-boss" : ""}`}
          style={{ backgroundImage: `url("${battleSceneForZone(run.zoneId)}")` }}
        >
          <div className="battle-topline">
            <div className="run-haul" aria-label="本局资源">
              <span title={`木材 ${run.wood}`} data-effect-target="wood">
                <ResourceIcon kind="wood" size={13} />
                <b>{run.wood}</b>
              </span>
              <span title={`矿石 ${run.ore}`} data-effect-target="ore">
                <ResourceIcon kind="ore" size={13} />
                <b>{run.ore}</b>
              </span>
              <span title={`金币 ${run.gold}`}>
                <ResourceIcon kind="gold" size={13} />
                <b>{run.gold}</b>
              </span>
              <span title={`结晶 ${run.crystal}`}>
                <ResourceIcon kind="crystal" size={13} />
                <b>{run.crystal}</b>
              </span>
            </div>
            <div className="depth-track" aria-label={`当前深度 ${run.depth} 层`}>
              <strong>{run.depth}</strong>
              <span>
                <i style={{ width: `${depthProgress}%` }} />
              </span>
              <small>{Math.ceil(run.depth / 10) * 10}</small>
            </div>
            <div className="battle-tools">
              <button
                type="button"
                className={`battle-tool${muted ? " is-active" : ""}`}
                onClick={onToggleMuted}
                title={muted ? "开启声音" : "静音"}
                aria-label={muted ? "开启声音" : "静音"}
                aria-pressed={muted}
              >
                {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <button
                className="retreat-button"
                aria-label="撤退"
                title={retreatLocked ? `${retreatRemainingSec} 秒后可撤退` : "撤退"}
                onClick={() => {
                  if (window.confirm("确认撤退？本局资源会保留，剩余生命按比例结算。")) retreat();
                }}
                disabled={busy || retreatLocked}
              >
                <DoorOpen size={18} />
                {retreatLocked && <span className="retreat-countdown">{retreatRemainingSec}</span>}
              </button>
            </div>
          </div>
          {run.encounter === "chest" ? (
            <ChestEncounter
              keys={run.keys}
              cost={run.chestCost ?? 3}
              onChoice={onChest}
              busy={busy}
            />
          ) : run.encounter === "obstacle" ? (
            <ObstacleEncounter
              progress={run.encounterProgress ?? 0}
              target={run.encounterTarget ?? 0}
              attempts={run.encounterAttempts ?? 0}
              maxAttempts={run.encounterMaxAttempts ?? 5}
            />
          ) : run.monster ? (
            <>
              <div className="encounter-flags">
                <span className="zone-flag">{run.zoneName}</span>
                {run.mutation?.id && (
                  <span className="mutation-chip" title={run.mutation.description}>
                    {run.mutation.name}
                  </span>
                )}
                {prepLeft > 0 && (
                  <span className="prep-flag">准备 {Math.ceil(prepLeft / 1000)}s</span>
                )}
                {run.encounter === "swarm" && (
                  <span className="swarm-flag">怪群 · 剩余 {run.encounterCount ?? 1} 只</span>
                )}
                {run.encounter === "elite" && <span className="elite-flag">精英遭遇</span>}
                {run.monster.boss && (
                  <span className="boss-flag">层主 · 阶段 {run.monster.phase}/3</span>
                )}
              </div>
              <div
                className={`monster-formation count-${visibleMonsters.length}`}
                data-effect-target="enemy"
              >
                {visibleMonsters.map((monster, index) => {
                  const monsterAtb = Math.max(
                    0,
                    Math.min(
                      100,
                      (1 - Math.max(0, monster!.nextAttackAt - clock) / monster!.attackPeriodMs) *
                        100,
                    ),
                  );
                  const traitSummary = monsterTraitSummary(
                    monster!,
                    view.machineConfig.reflectDamageAttackMultiplierCap,
                  );
                  const monsterHit = incomingMonsterIDs.has(monster!.id);
                  const monsterDamaged =
                    recentOutgoingDamage && run.lastDamageTarget === monster!.id;
                  return (
                    <article
                      className={`monster-row${monsterHit ? " is-attacking" : ""}${monsterDamaged ? " is-hit" : ""}`}
                      key={`${monster!.id}-${index}`}
                    >
                      <button
                        className="enemy-inspect-target"
                        aria-label={`查看${monster!.name}详情`}
                        title={`查看${monster!.name}详情`}
                        aria-haspopup="dialog"
                        onClick={() =>
                          setInspectedEnemy({ id: monster!.id, index, scope: enemyScope })
                        }
                      ></button>
                      <img
                        className="monster-sprite"
                        src={spriteForMonster(monster!)}
                        alt={monster!.name}
                      />
                      <div className="monster-panel">
                        <div className="monster-name">
                          <strong>
                            <span className="monster-title-text">{monster!.name}</span>
                            <TargetElementMark
                              resistance={monster!.resistanceElement}
                              weakness={monster!.weaknessElement}
                            />
                          </strong>
                          <span className="monster-armor">护甲 {monster!.armor}</span>
                        </div>
                        {traitSummary && (
                          <span className="monster-trait-label">
                            {(monster!.eliteTraits?.length ?? 0) > 0
                              ? `精英 · ${monster!.eliteTraits!.length} 词缀`
                              : "怪物特性"}
                          </span>
                        )}
                        <Meter
                          value={(Math.max(0, monster!.hp) / monster!.maxHp) * 100}
                          tone="enemy"
                        />
                        <div className="atb-row">
                          <span>ATB</span>
                          <Meter value={prepLeft > 0 ? 0 : monsterAtb} tone="atb" />
                          <strong>
                            {prepLeft > 0
                              ? "冻结"
                              : `${formatDisplayNumber(Math.max(0, (monster!.nextAttackAt - clock) / 1000))}s`}
                          </strong>
                        </div>
                      </div>
                      {monsterDamaged && (
                        <div
                          className={`monster-damage-number is-${run.lastElementOutcome ?? "neutral"}`}
                          key={`${run.lastDamageAt}-${run.lastDamageTarget}`}
                        >
                          -{run.lastDamage}
                          {run.lastElementOutcome === "weakness" ? (
                            <small>克制</small>
                          ) : run.lastElementOutcome === "resisted" ? (
                            <small>抵抗</small>
                          ) : null}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
              {run.message && (
                <aside className="battle-combat-message" role="status" aria-live="polite">
                  {run.message}
                </aside>
              )}
              {recentOutgoingDamage && !outgoingTargetVisible && (
                <div
                  className={`damage-feedback is-${run.lastElementOutcome ?? "neutral"}`}
                  key={`${run.lastDamageAt}-${run.lastDamageTarget}`}
                >
                  -{run.lastDamage}
                  {run.lastElementOutcome === "weakness" ? (
                    <small>克制</small>
                  ) : run.lastElementOutcome === "resisted" ? (
                    <small>抵抗</small>
                  ) : null}
                </div>
              )}
            </>
          ) : null}
        </section>

        <section className="combat-deck" aria-label={`${playerName}战斗状态与技能`}>
          <div
            className={`player-card${recentIncomingHits.some((hit) => hit.damage > 0) ? " is-damaged" : ""}${recentIncomingHits.some((hit) => hit.blocked > 0) ? " is-blocking" : ""}`}
          >
            <img src="/assets/sprites/breacher.png" alt={playerName} />
            <div className="player-vitals">
              <strong>{playerName}</strong>
              <AttackElementMark element={run.weaponElement ?? "neutral"} />
              <Meter value={(run.hp / run.maxHp) * 100} tone="hp" />
              <small>
                {run.hp} / {run.maxHp}
              </small>
            </div>
          </div>
          <IncomingHitFeedback hits={run.lastIncomingHits} now={clock} />
          <div className="status-strip">
            <Status
              icon={<Shield size={17} />}
              label="格挡"
              value={run.block}
              effectTarget="block"
            />
            <Status
              icon={<WandSparkles size={17} />}
              label="法力"
              value={`${run.mana}/${run.manaCap}`}
              effectTarget="mana"
            />
            <Status
              icon={<KeyRound size={17} />}
              label="钥匙"
              value={run.keys}
              effectTarget="key"
            />
          </div>
          <div className="skill-bar">
            {equippedSkills.map((skill) => {
              const cooldown = skillCooldownState(
                run.skillCooldowns,
                run.skillCooldownDurations,
                skill.id,
                clock,
                skill.cooldownMs,
              );
              return (
                <SkillButton
                  key={skill.id}
                  icon={<SkillIcon skillId={skill.id} />}
                  name={skill.name}
                  effectTarget={skill.target}
                  effectKind={
                    skill.target === "self" || skill.target === "all_allies"
                      ? "support"
                      : skill.branch === "warrior"
                        ? "strike"
                        : "magic"
                  }
                  effectElement={skill.damageElement === "weapon" ? "neutral" : skill.damageElement}
                  description={`${skill.description}${skillResourceLimitText(skill, run.skillResourceUseLimits?.[skill.id] ?? skill.resourceUseLimit)} · R${run.skillRanks?.[skill.id] ?? skill.rank}`}
                  cost={run.freeCastReady ? 0 : (run.skillManaCosts?.[skill.id] ?? skill.manaCost)}
                  cooldownRemainingMs={cooldown.remainingMs}
                  cooldownDurationMs={cooldown.durationMs}
                  disabled={
                    busy ||
                    requestQueued ||
                    cooldown.remainingMs > 0 ||
                    (!run.freeCastReady &&
                      run.mana < (run.skillManaCosts?.[skill.id] ?? skill.manaCost)) ||
                    (skill.id === "free_cast" && !!run.freeCastReady) ||
                    (!["combat", "swarm", "elite"].includes(run.encounter) &&
                      (!["obstacle", "chest"].includes(run.encounter) ||
                        !["self", "board"].includes(skill.target))) ||
                    (skill.id === "key_burst" && run.keys <= 0)
                  }
                  onClick={() => onSkill(skill.id)}
                />
              );
            })}
          </div>
        </section>
        <div className="combat-auxiliary">
          <div className="combat-mode-strip">
            <div
              className={`combo-slot${combo > 0 ? " is-active" : ""}${combo > 0 && comboProgress <= 25 ? " is-urgent" : ""}`}
              title={
                combo > 0
                  ? `手动连击 ${combo} 层，物理伤害 +${combo}%，剩余 ${comboRemainingSeconds} 秒`
                  : "手动有效消除可开始连击"
              }
              aria-label={`连击槽 ${combo} 层，物理伤害加成 ${combo}%，剩余 ${comboRemainingSeconds} 秒`}
            >
              <Zap size={16} aria-hidden="true" />
              <span className="combo-label">
                <b>连击槽</b>
                <small>{combo > 0 ? `${combo} 层 · ${comboRemainingSeconds}s` : "0 层"}</small>
              </span>
              <span
                className="combo-meter"
                role="progressbar"
                aria-label="连击剩余时间"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(comboProgress)}
                aria-valuetext={`剩余 ${comboRemainingSeconds} 秒`}
              >
                <i style={{ width: `${comboProgress}%` }} />
              </span>
              <strong>+{combo}%</strong>
            </div>
            <label
              className={`auto-mode-toggle${autoRequested ? " is-active" : ""}`}
              title={`每 ${formatDisplayNumber(automaticSlideIntervalMs / 1000, true)} 秒自动执行一个合法消除；宝箱会在钥匙足够时开启，否则跳过；自动消除每两次成功解算累积一层连击`}
            >
              <Bot size={16} aria-hidden="true" />
              <span>自动</span>
              <input
                type="checkbox"
                checked={autoRequested}
                onChange={(event) => {
                  const enabled = event.currentTarget.checked;
                  writeAutoModePreference(enabled);
                  setAutoMode(enabled);
                }}
              />
              <i aria-hidden="true" />
            </label>
          </div>
          {showGuestRegistrationPrompt && (
            <aside className="guest-registration-prompt" aria-live="polite">
              <UserRound size={20} aria-hidden="true" />
              <span>
                <strong>已抵达第 10 层</strong>
                <small>注册账号，保留这名 Breacher 的进度。</small>
              </span>
              <button className="guest-register-action" onClick={onBind}>
                <UserPlus size={15} />
                注册
              </button>
              <button
                className="guest-register-dismiss"
                onClick={() => setDismissedGuestPrompt(run.id)}
                title="关闭提示"
                aria-label="关闭注册提示"
              >
                <X size={16} />
              </button>
            </aside>
          )}
          {(Array.isArray(view.refillPreview) ? view.refillPreview : []).length > 0 && (
            <div className="refill-preview" aria-label="补牌预知">
              {(Array.isArray(view.refillPreview) ? view.refillPreview : []).map((tile, index) => (
                <i key={`${index}-${tile}`} className={`tile-swatch tile-${tile}`} />
              ))}
            </div>
          )}
          {run.mechanics.canLockRow && (
            <div className="row-lock-control" aria-label="选择锁定行">
              <LockKeyhole size={15} />
              {Array.from({ length: 7 }, (_, row) => (
                <button
                  key={row}
                  className={run.lockedRow === row ? "is-active" : ""}
                  disabled={busy || requestQueued || autoMode || run.rowLockUsed}
                  onClick={() => onLockRow(row)}
                  aria-label={`锁定第 ${row + 1} 行`}
                >
                  {row + 1}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="board-stage">
          <BoardCanvas
            ref={boardCanvasRef}
            board={run.board}
            boardHash={run.boardHash}
            revision={run.revision}
            cascades={run.lastCascades ?? []}
            transformations={run.lastTransformations ?? []}
            disabled={locked || autoMode}
            colorBlind={colorBlind}
            effectSettings={effectSettings}
            fallDelayMs={run.mechanics.fallDelayMs}
            lockedRow={
              (run.enemyLockedUntil ?? 0) > clock
                ? (run.enemyLockedRow ?? -1)
                : run.rowLockUsed
                  ? run.lockedRow
                  : -1
            }
            foggedCells={(run.fogUntil ?? 0) > clock ? (run.foggedCells ?? []) : []}
            wrapMatches={run.mechanics.wrapMatches}
            woodOreUnion={run.mechanics.woodOreUnion}
            queued={inputQueued || requestQueued}
            onSlide={submitOrQueueSlide}
          />
        </div>
      </section>
    </main>
  );
}
